import * as mqtt from "mqtt";

import {
  Capabilities,
  CssCapabilities,
  JsCapabilities,
  RenderingCapabilities,
  WebSocketCapabilities,
} from "types/analytics";
import { isCanvasSupported } from "utils/canvas";

/**
 * One-shot web platform feature probes used to decide what v2 can rely on.
 *
 * Everything here runs once per session and is written defensively: any probe
 * that cannot run returns null rather than throwing, since these browsers are
 * old enough that even `CSS.supports` may be missing.
 */

const isSoftwareRenderer = (renderer: string) =>
  /swiftshader|software|llvmpipe|softpipe|mesa offscreen|basic render|microsoft basic/i.test(
    renderer
  );

/** Probes a property/value pair, falling back to a style-object assignment. */
const cssSupports = (property: string, value: string): boolean | null => {
  try {
    if (window.CSS && typeof window.CSS.supports === "function") {
      return window.CSS.supports(property, value);
    }
  } catch (e) {
    // fall through to the style-object fallback
  }

  try {
    const style = document.createElement("div").style;
    style[property] = "";
    style[property] = value;
    return style[property] !== "";
  } catch (e) {
    return null;
  }
};

/** Probes a full `@supports` condition string. */
const cssSupportsCondition = (condition: string): boolean | null => {
  try {
    if (window.CSS && typeof window.CSS.supports === "function") {
      return window.CSS.supports(condition);
    }
  } catch (e) {
    // ignore
  }
  return null;
};

/**
 * Probes selector support by parsing it. Unsupported selectors throw a
 * SyntaxError, which is more widely reliable than `CSS.supports("selector(…)")`
 * (itself only available from Chromium 83).
 */
const selectorSupported = (selector: string): boolean => {
  try {
    document.querySelector(selector);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Flex `gap` needs a layout probe: the `gap` property can be supported for grid
 * while being ignored in flex containers (Chromium below 84).
 */
const supportsFlexGap = (): boolean | null => {
  if (!document.body) return null;

  let probe: HTMLDivElement | null = null;
  try {
    probe = document.createElement("div");
    probe.style.position = "absolute";
    probe.style.visibility = "hidden";
    probe.style.display = "flex";
    probe.style.flexDirection = "column";
    probe.style.rowGap = "1px";
    probe.appendChild(document.createElement("div"));
    probe.appendChild(document.createElement("div"));
    document.body.appendChild(probe);

    return probe.scrollHeight === 1;
  } catch (e) {
    return null;
  } finally {
    try {
      if (probe && probe.parentNode) probe.parentNode.removeChild(probe);
    } catch (e) {
      // ignore
    }
  }
};

/**
 * Evaluates a syntax snippet to probe language-level support. Returns null when
 * evaluation itself is unavailable (e.g. blocked by a CSP), which is different
 * from the syntax being unsupported.
 */
const syntaxSupported = (snippet: string): boolean | null => {
  try {
    // eslint-disable-next-line no-new-func
    new Function(snippet);
    return true;
  } catch (e) {
    return e instanceof SyntaxError ? false : null;
  }
};

/**
 * True only when the value is the engine's own implementation.
 *
 * `core-js/stable` is imported at startup, so an old WebView will happily report
 * `Array.prototype.flat` and friends as present. Native implementations
 * stringify to "[native code]" while polyfills expose their source, which is
 * what lets us tell the engine's real vintage from the polyfilled surface.
 */
const isNative = (value: any): boolean => {
  try {
    return (
      typeof value === "function" &&
      /\[native code\]/.test(Function.prototype.toString.call(value))
    );
  } catch (e) {
    return false;
  }
};

/** A built-in that exists but is not native has been polyfilled. */
const isPolyfilled = (value: any): boolean =>
  typeof value === "function" && !isNative(value);

/** Reported only — analytics itself never reads or writes web storage. */
const storageAvailable = (key: "localStorage" | "sessionStorage"): boolean => {
  try {
    return !!window[key];
  } catch (e) {
    // Access itself throws when storage is disabled
    return false;
  }
};

const getCssCapabilities = (): CssCapabilities => ({
  grid: cssSupports("display", "grid"),
  grid_template_fr: cssSupports("grid-template-columns", "1fr 1fr"),
  flexbox: cssSupports("display", "flex"),
  flex_gap: supportsFlexGap(),
  gap_property: cssSupports("gap", "1px"),
  is_selector: selectorSupported(":is(body)"),
  where_selector: selectorSupported(":where(body)"),
  has_selector: selectorSupported("body:has(div)"),
  not_selector_list: selectorSupported("body:not(div, span)"),
  custom_properties: cssSupportsCondition("(--a: 0)"),
  calc: cssSupports("width", "calc(1px + 1%)"),
  min_max_clamp: cssSupports("width", "clamp(1px, 2px, 3px)"),
  aspect_ratio: cssSupports("aspect-ratio", "1 / 1"),
  position_sticky: cssSupports("position", "sticky"),
  transform_3d: cssSupports("transform", "translate3d(0, 0, 0)"),
  transitions: cssSupports("transition", "all 1s"),
  backdrop_filter: cssSupports("backdrop-filter", "blur(1px)"),
  clip_path: cssSupports("clip-path", "inset(0)"),
  conic_gradient: cssSupports("background", "conic-gradient(#000, #fff)"),
  container_queries: cssSupports("container-type", "inline-size"),
  logical_properties: cssSupports("margin-inline-start", "1px"),
  object_fit: cssSupports("object-fit", "contain"),
  overscroll_behavior: cssSupports("overscroll-behavior", "contain"),
  supports_api: !!(window.CSS && typeof window.CSS.supports === "function"),
});

/** Built-ins core-js can backfill, checked for polyfill contamination */
const POLYFILLABLE_BUILTINS = () => [
  (window as any).Promise,
  (window as any).Symbol,
  (window as any).Map,
  (window as any).Set,
  (window as any).WeakMap,
  Object.assign,
  Object.entries,
  (Object as any).fromEntries,
  Array.prototype.includes,
  (Array.prototype as any).flat,
  (String.prototype as any).replaceAll,
];

const getJsCapabilities = (): JsCapabilities => ({
  builtins_polyfilled: POLYFILLABLE_BUILTINS().some(isPolyfilled),
  // These check for the engine's own implementation, not core-js's
  promise: isNative((window as any).Promise),
  symbol: isNative((window as any).Symbol),
  proxy: isNative((window as any).Proxy),
  weak_map: isNative((window as any).WeakMap),
  map_set: isNative((window as any).Map) && isNative((window as any).Set),
  object_assign: isNative(Object.assign),
  object_entries: isNative(Object.entries),
  object_from_entries: isNative((Object as any).fromEntries),
  array_includes: isNative(Array.prototype.includes),
  array_flat: isNative((Array.prototype as any).flat),
  string_replace_all: isNative((String.prototype as any).replaceAll),
  // whatwg-fetch polyfills fetch, so this is a native check too
  fetch: isNative(window.fetch),
  abort_controller: isNative((window as any).AbortController),
  intl: typeof Intl !== "undefined",
  intl_relative_time_format:
    typeof Intl !== "undefined" &&
    typeof (Intl as any).RelativeTimeFormat === "function",
  resize_observer: typeof (window as any).ResizeObserver !== "undefined",
  mutation_observer: typeof window.MutationObserver !== "undefined",
  intersection_observer:
    typeof (window as any).IntersectionObserver !== "undefined",
  request_animation_frame: typeof window.requestAnimationFrame === "function",
  performance_now:
    typeof performance !== "undefined" && typeof performance.now === "function",
  performance_observer:
    typeof (window as any).PerformanceObserver !== "undefined",
  performance_memory:
    typeof performance !== "undefined" && !!(performance as any).memory,
  web_workers: typeof Worker !== "undefined",
  service_worker: !!navigator && "serviceWorker" in navigator,
  custom_elements: typeof (window as any).customElements !== "undefined",
  shadow_dom: typeof Element.prototype.attachShadow === "function",
  local_storage_available: storageAvailable("localStorage"),
  session_storage_available: storageAvailable("sessionStorage"),
  es6_syntax: syntaxSupported("class A {}; const b = () => {}; b();"),
  async_await: syntaxSupported("(async () => {})"),
  optional_chaining: syntaxSupported("const a = {}; a?.b;"),
  nullish_coalescing: syntaxSupported("const a = null ?? 1;"),
});

const getWebSocketCapabilities = (
  client: mqtt.MqttClient | null
): WebSocketCapabilities => {
  const supported = typeof WebSocket !== "undefined";
  const options: any = client?.options ?? {};

  return {
    supported,
    binary_type:
      supported && !!WebSocket.prototype && "binaryType" in WebSocket.prototype,
    array_buffer: typeof ArrayBuffer !== "undefined",
    blob: typeof Blob !== "undefined",
    close_event: typeof CloseEvent !== "undefined",
    page_protocol: window?.location?.protocol ?? null,
    broker_protocol: options.protocol ?? null,
    broker_host: options.hostname ?? null,
    broker_port: typeof options.port === "number" ? options.port : null,
    broker_path: options.path ?? null,
    secure: options.protocol ? options.protocol === "wss" : null,
    mqtt_protocol_version:
      typeof options.protocolVersion === "number"
        ? options.protocolVersion
        : null,
    mqtt_keepalive_seconds:
      typeof options.keepalive === "number" ? options.keepalive : null,
    connected: client ? !!client.connected : null,
  };
};

const getRenderingCapabilities = (
  browserSpeed: number | null
): RenderingCapabilities => {
  const capabilities: RenderingCapabilities = {
    canvas_2d: isCanvasSupported(),
    canvas_filter: false,
    webgl: false,
    webgl2: false,
    webgl_vendor: null,
    webgl_renderer: null,
    hardware_accelerated: null,
    max_texture_size: null,
    svg: false,
    browser_speed: browserSpeed,
  };

  try {
    capabilities.svg = !!(
      document.createElementNS &&
      document.createElementNS("http://www.w3.org/2000/svg", "svg")
        .createSVGRect
    );
  } catch (e) {
    capabilities.svg = false;
  }

  try {
    if (capabilities.canvas_2d) {
      const ctx = document.createElement("canvas").getContext("2d");
      capabilities.canvas_filter = !!ctx && (ctx as any).filter !== undefined;
    }
  } catch (e) {
    // ignore
  }

  try {
    // A canvas can only ever hold one context type, so webgl2 needs its own
    capabilities.webgl2 = !!document
      .createElement("canvas")
      .getContext("webgl2");
  } catch (e) {
    // ignore
  }

  try {
    const canvas = document.createElement("canvas");
    const gl: any =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");

    if (gl) {
      capabilities.webgl = true;
      capabilities.max_texture_size = gl.getParameter(gl.MAX_TEXTURE_SIZE);

      const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
      if (debugInfo) {
        capabilities.webgl_vendor =
          gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) ?? null;
        capabilities.webgl_renderer =
          gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) ?? null;
      } else {
        capabilities.webgl_vendor = gl.getParameter(gl.VENDOR) ?? null;
        capabilities.webgl_renderer = gl.getParameter(gl.RENDERER) ?? null;
      }

      if (capabilities.webgl_renderer) {
        capabilities.hardware_accelerated = !isSoftwareRenderer(
          capabilities.webgl_renderer
        );
      }

      // Release the context immediately — low-tier units should not keep a GPU
      // context open just so we can read its renderer string
      const loseContext = gl.getExtension("WEBGL_lose_context");
      if (loseContext) loseContext.loseContext();
    }
  } catch (e) {
    // ignore
  }

  return capabilities;
};

export const getCapabilities = (
  client: mqtt.MqttClient | null,
  browserSpeed: number | null
): Capabilities => ({
  css: getCssCapabilities(),
  js: getJsCapabilities(),
  websocket: getWebSocketCapabilities(client),
  rendering: getRenderingCapabilities(browserSpeed),
});
